import { formatDatetimeWithSeconds } from 'src/utils/dateUtils';
import { useMemo } from 'react';
import { ProcedureExecutionEvidence } from './ProcedureExecutionEvidence';
import {
  ActionCommandSection,
  ActorBadge,
  ProcedureRequestSection,
  SectionHeading,
  StepInstructionSection,
  ThinkingSection,
} from './TraceDetailSections';
import { commandArgsOf, procedureRequestOf } from './traceDetail';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from 'src/components/ui/tabs';
import { TraceScreenshot } from './TraceScreenshot';
import {
  BrainIcon,
  HandPalmIcon,
  WrenchIcon,
  RobotIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { cn } from 'src/lib/utils';
import { CodeBlock, MetaRow } from './CodeBlock';
import { StatusDot } from './atoms';
import type { Span, SpanHumanTakeover } from './types';

/** What kind of thing the header is describing — one classification drives glyph, tint, label. */
type HeaderKind = 'human' | 'subagent' | 'tool' | 'llm';

/** Classify a trace header so its label, icon, and color describe the same entity. */
function headerKind(span: Span, isSubagent: boolean): HeaderKind {
  if (span.human) return 'human';
  if (isSubagent) return 'subagent';
  return span.type === 'tool' ? 'tool' : 'llm';
}

const HEADER_TILE_CLASS: Record<HeaderKind, string> = {
  human: 'bg-amber-50 border-amber-200',
  subagent: 'bg-indigo-50 border-indigo-200',
  tool: 'bg-[#fbf6ec] border-[#e8dcc4]',
  llm: 'bg-stone-100 border-stone-200',
};

/** Render the icon corresponding to the trace header classification. */
function HeaderGlyph({ kind }: { kind: HeaderKind }) {
  switch (kind) {
    case 'human':
      return (
        <HandPalmIcon weight="fill" className="h-3.5 w-3.5 text-amber-700" />
      );
    case 'subagent':
      return <RobotIcon className="h-3.5 w-3.5 text-indigo-600" />;
    case 'tool':
      return <WrenchIcon className="h-3.5 w-3.5 text-stone-600" />;
    case 'llm':
      return <BrainIcon className="h-3.5 w-3.5 text-stone-600" />;
  }
}

/** Translate the trace entity type for the detail header. */
function headerKindLabel(
  kind: HeaderKind,
  gt: ReturnType<typeof useGT>,
): string {
  switch (kind) {
    case 'human':
      return gt('Human', {
        context:
          'agent trace span type label in detail panel header, a person intervening in the run',
      });
    case 'subagent':
      return gt('Subagent', {
        context:
          'agent trace span type label in detail panel header, a subagent whose handler runs its own LLM loop',
      });
    case 'tool':
      return gt('Tool', {
        context:
          'agent trace span type label in detail panel header, tool call',
      });
    case 'llm':
      return gt('LLM', {
        context: 'agent trace span type label in detail panel header, LLM step',
      });
  }
}

/** The header's title: a human's row is named for the person's act, every other row for itself. */
function headerTitle(span: Span, gt: ReturnType<typeof useGT>): string {
  return span.human
    ? gt('Human takeover', {
        context:
          'agent trace detail panel title for a person pausing and stepping into a live operator run',
      })
    : span.name;
}

/** Type + status + name + preview + the close control: which span you are looking at. */
function SpanDetailHeader({
  span,
  isSubagent,
  onClose,
  gt,
}: {
  span: Span;
  isSubagent: boolean;
  onClose: () => void;
  gt: ReturnType<typeof useGT>;
}) {
  const kind = headerKind(span, isSubagent);
  return (
    <div className="px-3.5 py-3 border-b border-border shrink-0">
      <div className="flex items-start gap-2.5">
        <div
          className={cn(
            'w-7 h-7 rounded-md border flex items-center justify-center shrink-0',
            HEADER_TILE_CLASS[kind],
          )}
        >
          <HeaderGlyph kind={kind} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[9.5px] font-semibold tracking-widest uppercase text-stone-500">
              {headerKindLabel(kind, gt)}
            </span>
            {span.actor && <ActorBadge actor={span.actor} />}
            <span className="text-[9.5px] text-stone-300">·</span>
            <div className="flex items-center gap-1 text-[11px] text-stone-700">
              <StatusDot status={span.status} />
              {span.status}
            </div>
          </div>
          <div
            className={cn(
              'text-sm font-semibold text-stone-900 break-words leading-snug',
              'font-mono',
            )}
          >
            {headerTitle(span, gt)}
          </div>
          {span.preview && !span.preview.trim().startsWith('{') && (
            <p className="text-xs text-muted-foreground mt-1 leading-snug line-clamp-2">
              {span.preview}
            </p>
          )}
        </div>
        <button
          type="button"
          aria-label={gt('Close details panel', {
            context: 'aria label for closing agent trace span detail panel',
          })}
          onClick={onClose}
          className="p-1 rounded border-none bg-transparent text-muted-foreground cursor-pointer hover:bg-stone-100 shrink-0"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

interface SpanDetailPanelProps {
  span: Span | null;
  onClose: () => void;
  /**
   * Render for a slot directly beneath the span's own row rather than as a standalone pane:
   * no header (the row above it already names the span and carries the close affordance), and
   * no `h-full`/inner scroller, so the block grows with its content inside the tree's scroller.
   */
  embedded?: boolean;
  onRetryScreenshot?: () => Promise<unknown>;
}

/** Format a recorded takeover timestamp for the technical detail panel. */
function fmtClock(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleTimeString();
}

const HUMAN_OUTCOME_CONTEXT =
  'agent trace detail panel value for how a human-takeover pause ended';

/** The human-takeover facts: mode, who, when, the instruction and the brief the operator got. */
function HumanTakeoverSection({
  human,
  gt,
}: {
  /** Null on every span that isn't a human intervention — the section renders nothing. */
  human: SpanHumanTakeover | null;
  gt: ReturnType<typeof useGT>;
}) {
  if (!human) return null;
  const outcome: Record<SpanHumanTakeover['outcome'], string> = {
    paused: gt('Paused — waiting for a person', {
      context: HUMAN_OUTCOME_CONTEXT,
    }),
    instruct: gt('Typed an instruction', { context: HUMAN_OUTCOME_CONTEXT }),
    takeover: gt('Drove the desktop, then handed control back', {
      context: HUMAN_OUTCOME_CONTEXT,
    }),
    cancelled: gt('Run stopped while paused', {
      context: HUMAN_OUTCOME_CONTEXT,
    }),
    timed_out: gt('Nobody answered before the pause timed out', {
      context: HUMAN_OUTCOME_CONTEXT,
    }),
  };
  const pausedAt = fmtClock(human.pausedAt);
  const resolvedAt = fmtClock(human.resolvedAt);
  return (
    <div>
      <p className="text-[10px] font-semibold tracking-widest uppercase text-stone-500 mb-1.5">
        {gt('Human takeover', {
          context:
            'agent trace span detail section heading for a person intervening in the run',
        })}
      </p>
      <div className="bg-amber-50/60 border border-amber-200 rounded-lg px-2.5 py-1">
        <MetaRow
          label={gt('Outcome', {
            context: 'agent trace human-takeover field label',
          })}
          value={outcome[human.outcome]}
        />
        {human.byName && (
          <MetaRow
            label={gt('By', {
              context:
                'agent trace human-takeover field label, the person who intervened',
            })}
            value={human.byName}
          />
        )}
        {pausedAt && (
          <MetaRow
            label={gt('Paused at', {
              context: 'agent trace human-takeover field label',
            })}
            value={pausedAt}
            mono
          />
        )}
        {resolvedAt && (
          <MetaRow
            label={gt('Resumed at', {
              context: 'agent trace human-takeover field label',
            })}
            value={resolvedAt}
            mono
          />
        )}
        {human.instruction && (
          <div className="py-1.5">
            <p className="text-[10px] font-semibold tracking-wide uppercase text-stone-500 mb-1">
              {gt('Instruction', {
                context:
                  'agent trace human-takeover field label, what the person typed',
              })}
            </p>
            <p className="whitespace-pre-wrap text-[11.5px] leading-relaxed text-stone-800">
              {human.instruction}
            </p>
          </div>
        )}
        {human.injectedText && (
          <div className="py-1.5">
            <p className="text-[10px] font-semibold tracking-wide uppercase text-stone-500 mb-1">
              {gt('Handed to the operator', {
                context:
                  'agent trace human-takeover field label, the brief injected into the operator on resume',
              })}
            </p>
            <CodeBlock maxHeight={160}>{human.injectedText}</CodeBlock>
          </div>
        )}
      </div>
    </div>
  );
}

/** Serialize the complete selected span once per identity, including its child timelines. */
export function SpanDetailPanel({
  span,
  onClose,
  embedded = false,
}: SpanDetailPanelProps) {
  const gt = useGT();
  const metadata = useMemo(() => JSON.stringify(span, null, 2), [span]);
  if (!span)
    return (
      <p className="p-3 text-sm text-muted-foreground">
        {gt('Select an action to inspect its details.', {
          context: 'empty technical trace details',
        })}
      </p>
    );
  return (
    <div className="flex min-w-0 flex-col gap-3 p-3">
      {!embedded && (
        <SpanDetailHeader
          span={span}
          isSubagent={Boolean(span.isSubagent)}
          onClose={onClose}
          gt={gt}
        />
      )}
      <HumanTakeoverSection human={span.human ?? null} gt={gt} />
      <CodeBlock
        label={gt('Recorded Metadata', { context: 'technical trace metadata' })}
      >
        {metadata}
      </CodeBlock>
    </div>
  );
}

/** The span's recorded failure reason: its `stopReason`, else its output's `error` (older rows). */
function failureReasonOf(span: Span): string | null {
  const outputError = span.toolOutput?.error;
  return (
    span.stopReason?.trim() ||
    (typeof outputError === 'string' ? outputError.trim() : '') ||
    null
  );
}

/**
 * The Result tab: what the span was told, why it acted, exactly what it did, and the model's
 * thinking — the facts a reader otherwise had to dig out of raw JSON or a separate tracing tool.
 */
function ResultSections({
  span,
  gt,
}: {
  span: Span;
  gt: ReturnType<typeof useGT>;
}) {
  const request = procedureRequestOf(span.toolInput);
  // A brief's headline IS the instruction the request section already shows in full.
  const why =
    span.text ||
    span.reasoning ||
    (request?.instruction ? null : span.brief?.headline);
  // A failed span with no prose (a setup/teardown workflow phase, a lifecycle tool) otherwise read
  // "No written result" here, with its reason only inside the raw Output JSON.
  const failureReason = span.status === 'failed' ? failureReasonOf(span) : null;
  const hasDetail = Boolean(
    failureReason ||
    span.isSopProcedure ||
    span.sopStepDescription ||
    span.sopBranch ||
    span.thinking ||
    request ||
    commandArgsOf(span),
  );
  return (
    <>
      {span.isSopProcedure && <ProcedureExecutionEvidence span={span} />}
      <StepInstructionSection span={span} />
      <ProcedureRequestSection span={span} />
      {failureReason && (
        <div>
          <SectionHeading>
            {gt('Failure reason', {
              context:
                'trace inspector section: the recorded reason this action failed',
            })}
          </SectionHeading>
          <p className="whitespace-pre-wrap text-red-700">{failureReason}</p>
        </div>
      )}
      {why ? (
        <div>
          {!span.isSopProcedure && (
            <SectionHeading>
              {gt('Why', {
                context:
                  'trace inspector section: the reasoning or result text behind this action',
              })}
            </SectionHeading>
          )}
          <p className="whitespace-pre-wrap">{why}</p>
        </div>
      ) : (
        !hasDetail && (
          <p>
            {gt('No written result was recorded for this action.', {
              context: 'trace inspector missing prose result',
            })}
          </p>
        )
      )}
      <ActionCommandSection span={span} />
      <ThinkingSection thinking={span.thinking} />
    </>
  );
}

/** Keep the evidence readable; payloads and diagnostics are explicit destinations. */
export function SpanInspector({
  span,
  onClose,
  embedded = false,
  onRetryScreenshot,
}: SpanDetailPanelProps) {
  const gt = useGT();
  if (!span)
    return (
      <SpanDetailPanel span={span} onClose={onClose} embedded={embedded} />
    );
  return (
    <div
      className={cn(
        'flex min-h-0 min-w-0 flex-col bg-background',
        !embedded && 'h-full',
      )}
    >
      {!embedded && (
        <SpanDetailHeader
          span={span}
          isSubagent={!!span.agentCard || !!span.isSubagent}
          onClose={onClose}
          gt={gt}
        />
      )}
      {span.operatorEvidence && (
        <p className="px-3 pt-2 text-xs text-muted-foreground">
          {formatDatetimeWithSeconds(span.operatorEvidence.record.createdAt)} ·{' '}
          {gt('Recorded step {index}', {
            context: 'original operator step index',
            index: String(span.operatorEvidence.record.step.index),
          })}
        </p>
      )}
      <Tabs
        key={span.id}
        defaultValue={span.screenshotSignedUrl ? 'screenshot' : 'result'}
        className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-auto p-3"
      >
        <TabsList className="h-auto flex-wrap justify-start">
          <TabsTrigger value="result">
            {gt('Result', { context: 'trace inspector tab' })}
          </TabsTrigger>
          <TabsTrigger value="screenshot">
            {gt('Screenshot', { context: 'trace inspector tab' })}
          </TabsTrigger>
          <TabsTrigger value="input">
            {gt('Input', { context: 'trace inspector tab' })}
          </TabsTrigger>
          <TabsTrigger value="output">
            {gt('Output', { context: 'trace inspector tab' })}
          </TabsTrigger>
          <TabsTrigger value="technical">
            {gt('Technical Details', { context: 'trace inspector tab' })}
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="result"
          className="flex min-w-0 flex-col gap-3 break-words"
        >
          <ResultSections span={span} gt={gt} />
        </TabsContent>
        <TabsContent value="screenshot" className="min-w-0">
          {span.screenshotSignedUrl ? (
            <TraceScreenshot
              key={span.screenshotSignedUrl}
              url={span.screenshotSignedUrl}
              onRetry={onRetryScreenshot}
              fitToViewport
            />
          ) : (
            gt('No screenshot was recorded for this action.', {
              context: 'trace inspector missing screenshot',
            })
          )}
        </TabsContent>
        <TabsContent value="input" className="min-w-0">
          <CodeBlock>{JSON.stringify(span.toolInput ?? {}, null, 2)}</CodeBlock>
        </TabsContent>
        <TabsContent value="output" className="min-w-0">
          <CodeBlock>
            {JSON.stringify(span.toolOutput ?? {}, null, 2)}
          </CodeBlock>
        </TabsContent>
        <TabsContent value="technical" className="min-w-0">
          <SpanDetailPanel span={span} onClose={onClose} embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
